import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TradingActionBoard from './TradingActionBoard';
import IstClock from './IstClock';
import './NormalTrading.css';
import './NormalTradingV1.css';

const API = '/api/best-performer';
const POLL_MS = 2000;

export default function BestPerformer() {
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const load = useCallback(async (warm = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await axios.get(`${API}/live-board`, {
        timeout: 20000,
        params: warm ? { warm: 1 } : undefined
      });
      setBoard(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Board load failed');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const wipe = useCallback(async () => {
    if (!window.confirm('Wipe Best Performer desk?\n\nClears BUY / HOLD / SELL / Sold Today.\nResets capital to ₹4,00,000 · tiered +₹/share by entry price.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/wipe`, {}, { timeout: 15000 });
      await load(true);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Wipe failed');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const lists = board?.actionLists || {};
  const buy = lists.buy || [];
  const hold = lists.hold || [];
  const sell = lists.sell || [];
  const soldToday = lists.soldToday || [];
  const profitBands = board?.profitBands || [];
  const profitLabel = board?.profitLabel === 'tiered'
    ? 'tiered ₹0.40–₹2.30'
    : (board?.profitLabel || 'tiered');
  const profitRs = Number(board?.profitRs) || 0.5;
  const cap = board?.capital || {};
  const basket = board?.basket || {};

  const activeStep = useMemo(() => {
    if (sell.length) return 2;
    if (hold.length) return 1;
    return 0;
  }, [sell.length, hold.length]);

  return (
    <div className="nt-desktop nt-v1 nt-no-sidebar">
      <main className="nt-main">
        <header className="nt-header nt-header-compact">
          <div>
            <h1>Best Performer</h1>
            <p className="nt-subtitle">
              ₹4L · <strong>uptrend charts only</strong> · profit by entry price · HOLD total &gt; ₹1 → SELL · 10s → Sold Today
            </p>
            {profitBands.length > 0 && (
              <p className="nt-subtitle" style={{ marginTop: 4, fontSize: '0.78rem', opacity: 0.9 }}>
                {profitBands.map((b) => b.label).join(' · ')}
              </p>
            )}
          </div>
          <div className="nt-v1-master-clock">
            <IstClock />
          </div>
          <div className="nt-v1-header-links" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/NormalTrading" className="nt-v1-btn">← V1 board</Link>
            <Link to="/Report?screen=best-performer" className="nt-v1-btn">Report</Link>
            <button type="button" className="nt-v1-btn primary" onClick={() => load(true)} disabled={busy}>
              Refresh
            </button>
            <button type="button" className="nt-v1-btn danger" onClick={wipe} disabled={busy}>
              Wipe desk
            </button>
          </div>
        </header>

        {error && <div className="nt-v1-master-error">{error}</div>}

        <div className="nt-v1-status-strip" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Status: <strong>{board?.status || '…'}</strong></span>
          <span>Wallet: <strong>₹{Number(cap.walletTotal || 400000).toLocaleString('en-IN')}</strong></span>
          <span>Free: <strong>₹{Number(cap.capitalAvailable || 0).toLocaleString('en-IN')}</strong></span>
          <span>In use: <strong>₹{Number(cap.capitalInUse || 0).toLocaleString('en-IN')}</strong></span>
          <span>Day P&amp;L: <strong>₹{Number(board?.dailyPnl || 0).toFixed(2)}</strong></span>
          <span>Open: <strong>{basket.slotsUsed || 0}</strong> names</span>
          <span style={{ opacity: 0.85 }}>{board?.lastAction || ''}</span>
        </div>

        <TradingActionBoard
          buy={buy}
          hold={hold}
          sell={sell}
          sellActive={sell}
          sellHistory={soldToday}
          bestPerformersToday={null}
          stopLossToday={[]}
          shortToday={[]}
          hideSecondaryTabs
          sellUrgentCount={sell.filter((s) => s.liveSellAlert || s.sellNow).length}
          sellTodayCount={soldToday.length}
          activeStep={activeStep}
          profitLabel={profitLabel}
          profitRs={profitRs}
          holdPartialMin={0.3}
          holdPartialMax={2}
          holdMinutes={10}
          stockQty={10}
          slotsMax={basket.slotsMax || 999}
          volatileMode
          quantSlotCount={basket.slotsUsed || 0}
          liveStatus={board?.marketOpen ? 'live' : 'closed'}
          fullyAutomated
          slotsFree={basket.slotsFree}
          holdSlotCount={hold.length}
        />
      </main>
    </div>
  );
}
